import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import AppContext from './context/AppContext';
import { Link } from 'react-router-dom';
import { MdEmail, MdOutlineArrowForwardIos } from 'react-icons/md';
import useDynamicPath from '../hooks/useDynamicPath';
import constants from '../constants.json' assert { type: 'json' };
import WeAccept from './WeAccept';
import { FaLocationDot } from 'react-icons/fa6';
import { IoCall } from 'react-icons/io5';
import '../styles/Footer.css';

const FOOTER_LINK_CLASS = 'footer-link';
const FOOTER_ICON_CLASS = 'footer-icon';
const FOOTER_COLUMN_CLASS = 'footer-column';
const FOOTER_ICON_CLASS_CONTAINER = 'footer-icon-container';
const DEFAULT_LIMIT = 5;

const Footer = () => {
    const [collectionLinks, setCollectionLinks] = useState([]);
    const collectionPath = useDynamicPath({ pathName: constants.DYNAMIC_PATHS.COLLECTION_PATH });
    const [brandLinks, setBrandLinks] = useState([]);
    const { appContext } = useContext(AppContext);
    const date = new Date();
    const appConfig = appContext.config.themeConfig;
    const phoneNumber = useMemo(
        () => appContext.config.contactPhoneNumber,
        [appContext.config.contactPhoneNumber],
    );
    const storeEmail = useMemo(
        () => appContext.config.storeEmail,
        [appContext.config.storeEmail],
    );

    const fetchCollections = useCallback(async () => {
        try {
            const url = new URL('api/nav', process.env.APP_URL);
            url.searchParams.append('collectionUrlPrefix', collectionPath[0].pathBeforeParams);
            url.searchParams.append('imagePlacement', appConfig.imagePlacements?.collection?.menuImage);
            url.searchParams.append('depth', 2);
            const response = await fetch(url);
            const data = await response.json();
            setCollectionLinks(data.navData || []);
        } catch (e) {
            console.info(e);
        }
    }, []);

    const fetchBrands = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            params.append('page', 0);
            params.append('limit', DEFAULT_LIMIT);
            params.append('sortBy', 'PRODUCT_COUNT');
            params.append('order', 'DESC');
            const response = await fetch(new URL(`api/products/vendors/fullDetail?${params}`, process.env.APP_URL));
            const data = await response.json();
            setBrandLinks(data.vendors ?? []);
        } catch (e) {
            console.info(e);
        }
    }, []);

    useEffect(() => {
        fetchCollections();
        fetchBrands();
    }, []);

    return (
        <footer
            className={'footer'}>
            <div
                className={'footer-container'}>
                <div
                    className={'footer-columns'}>
                    <div
                        className={FOOTER_COLUMN_CLASS}>
                        <Link
                            to={'/'}>
                            <img
                                src={appContext.config.storeLogoUrl}
                                className={'d-inline-block p-2 w-100 align-top nav-logo'}
                                style={{
                                    transition: '0.4s width ease-in-out',
                                    objectFit: 'contain',
                                    background: 'white',
                                }}
                                height={56}
                                alt={appContext.config.storeName}
                            />
                        </Link>
                        <br />
                        <br />
                        <p>
                            If you need a complete solution or a replacement part to all
                            your pool and spa needs (such as hot tub heaters and pool cleaners),
                            we are your reliable one-stop shop.
                        </p>
                        <br />
                        <p
                            className={'m-0'}><strong>Shop With Confidence</strong></p>
                        <img
                            className={'rating-badge'}
                            src={'https://www.shopperapproved.com/seal/14021/default-sa-seal.gif'}
                            alt={'4.6 stars'} />
                    </div>
                    <div
                        className={FOOTER_COLUMN_CLASS}>
                        <h6>Contact US</h6>
                        <p
                            className={'m-0 p-0 d-flex gap-2 align-items-start'}>
                            <div
                                className={FOOTER_ICON_CLASS_CONTAINER}>
                                <FaLocationDot
                                    className={FOOTER_ICON_CLASS}
                                    size={20} />
                            </div>
                            <p
                                className={'m-0 p-0'}>
                                Addresses : 1733 H St
                                Ste 450
                                Blaine, WA 98230
                            </p>
                        </p>
                        <br />
                        <p
                            className={'m-0 p-0 d-flex gap-2 align-items-center'}>
                            <div
                                className={FOOTER_ICON_CLASS_CONTAINER}>
                                <IoCall
                                    className={FOOTER_ICON_CLASS}
                                    size={20} />
                            </div>
                            <p
                                className={'m-0 p-0'}>
                                Call us now :  <a
                                    href={`tel:${phoneNumber}`}>{phoneNumber}</a>
                            </p>
                        </p>
                        <br />
                        <p
                            className={'m-0 p-0 d-flex gap-2 align-items-center'}>
                            <div
                                className={FOOTER_ICON_CLASS_CONTAINER}>
                                <MdEmail
                                    className={FOOTER_ICON_CLASS}
                                    size={20} />
                            </div>
                            <p
                                className={'m-0 p-0'}>
                                Email : <a
                                    className={'text-white'}
                                    href={`mailto:${storeEmail}`}>
                                    {storeEmail}
                                </a>
                            </p>
                        </p>
                    </div>
                    <div
                        className={FOOTER_COLUMN_CLASS}>
                        <h6>Information</h6>
                        <ul>
                            {appConfig?.footerNavLinks?.information?.map((link) => {
                                return (
                                    <li
                                        key={`nav-${link.url}-${link.text}`}><Link
                                            className={'footer-link'}
                                            to={link.url}>
                                            <MdOutlineArrowForwardIos
                                                size={12} /> {link.text}</Link></li>
                                );
                            })}
                        </ul>
                    </div>
                    <div
                        className={FOOTER_COLUMN_CLASS}>
                        <h6>BRANDS</h6>
                        <ul>
                            {brandLinks.map((brand, i) => (
                                <li
                                    key={`${i}-${brand.name}`}>
                                    <Link
                                        className={FOOTER_LINK_CLASS}
                                        to={`/brands/${brand.name}`}>
                                        <MdOutlineArrowForwardIos
                                            size={12} /> {brand.name}
                                    </Link>
                                </li>
                            ))}
                            <li>
                                <Link
                                    className={FOOTER_LINK_CLASS}
                                    to={'/brands'}>
                                    <MdOutlineArrowForwardIos
                                        size={12} /> All Brands</Link></li>
                        </ul>
                    </div>
                    <div
                        className={FOOTER_COLUMN_CLASS}>
                        <h6>ALL CATEGORIES</h6>
                        <ul>
                            {collectionLinks.map((link) => (
                                <li
                                    key={`collection-${link.url}-${link.title}`}><Link
                                        className={FOOTER_LINK_CLASS}
                                        to={link.url}>
                                        <MdOutlineArrowForwardIos
                                            size={12} /> {link.title}</Link></li>
                            ))}
                            <li>
                                <Link
                                    className={FOOTER_LINK_CLASS}
                                    to={collectionPath[0].pathBeforeParams}>
                                    <MdOutlineArrowForwardIos
                                        size={12} /> All Categories</Link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
            <div
                className={'footer-bottom'}>
                <p
                    className={'m-0'}>&copy; {date.getFullYear()} Pool and Spa Parts Superstore.</p>
                <div
                    className={'payment-icons'}>
                    <WeAccept />
                    <p
                        className={'m-0'}>This site is protected by reCAPTCHA and the Google <a
                            href={'https://policies.google.com/privacy'}>Privacy Policy</a> and <a
                            href={'https://policies.google.com/terms'}>Terms of Service</a> apply.
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
